import { useState } from 'react'
import {
  Calendar,
  Clock,
  MapPin,
  CheckCircle,
  XCircle,
  RefreshCw,
  Plus,
} from 'lucide-react'
import { Button } from '@components/ui/button'
import { Card, CardContent } from '@components/ui/card'
import { usePortalAppointments, useRequestAppointment } from '@hooks/usePortal'
import { useToast } from '@hooks/use-toast'
import { useClinicFormat } from '@context/ClinicTimezoneContext'
import { cn } from '@lib/utils'

const appointmentStatusLabels: Record<string, string> = {
  pending_approval: 'Pendiente de Aprobación',
  scheduled: 'Programada',
  confirmed: 'Confirmada',
  completed: 'Completada',
  cancelled: 'Cancelada',
  rescheduled: 'Reprogramada',
  pending: 'Pendiente',
  rejected: 'Rechazada',
}

const appointmentTypeLabels: Record<string, string> = {
  consultation: 'Consulta',
  follow_up: 'Seguimiento',
  checkup: 'Chequeo anual',
  telemedicine: 'Telemedicina',
}

export function PortalAppointmentsPage() {
  const { toast } = useToast()
  const { formatDateTime } = useClinicFormat()
  const { data: appointments, isLoading } = usePortalAppointments()
  const requestAppointment = useRequestAppointment()

  const [showScheduleModal, setShowScheduleModal] = useState(false)

  const handleRequestAppointment = async (data: {
    preferred_date: string
    appointment_type: string
    reason: string
  }) => {
    try {
      await requestAppointment.mutateAsync({
        preferred_date: data.preferred_date,
        appointment_type: data.appointment_type,
        reason: data.reason,
        is_urgent: false,
      })
      toast({
        title: 'Cita solicitada',
        description: 'Tu solicitud de cita fue enviada para revisión.',
      })
      setShowScheduleModal(false)
    } catch (error) {
      toast({
        title: 'No se pudo enviar la solicitud',
        description: 'Inténtalo de nuevo más tarde.',
        variant: 'destructive',
      })
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'cancelled':
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-600" />
      case 'confirmed':
        return <CheckCircle className="h-4 w-4 text-blue-600" />
      default:
        return <Clock className="h-4 w-4 text-amber-600" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'cancelled':
      case 'rejected':
        return 'bg-red-100 text-red-800'
      case 'confirmed':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-amber-100 text-amber-800'
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Mis citas</h1>
          <p className="text-muted-foreground mt-1">Consulta y gestiona tus citas</p>
        </div>
        <Button onClick={() => setShowScheduleModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Solicitar cita
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="h-24 animate-pulse"></Card>
          ))}
        </div>
      ) : !appointments?.length ? (
        <Card className="p-8 text-center">
          <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Sin citas</h3>
          <p className="text-muted-foreground mb-4">
            No tienes citas programadas.
          </p>
          <Button onClick={() => setShowScheduleModal(true)}>
            Solicitar mi primera cita
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {appointments.map((appointment) => (
            <Card key={appointment.id}>
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-lg bg-medical-100 flex items-center justify-center flex-shrink-0">
                      <Calendar className="h-6 w-6 text-medical-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">
                        {appointmentTypeLabels[appointment.appointment_type] ?? appointment.appointment_type}
                      </h3>
                      <p className="text-muted-foreground">
                        Dr. {appointment.doctor_name}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {formatDateTime(appointment.appointment_date)}
                        </span>
                        {appointment.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {appointment.location}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={cn(
                        'px-3 py-1 rounded text-sm font-medium flex items-center gap-1.5',
                        getStatusColor(appointment.status)
                      )}
                    >
                      {getStatusIcon(appointment.status)}
                      {appointmentStatusLabels[appointment.status] ?? appointment.status}
                    </span>
                    {appointment.can_cancel && (
                      <Button variant="outline" size="sm">
                        Cancelar
                      </Button>
                    )}
                    {appointment.can_reschedule && (
                      <Button variant="outline" size="sm">
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Reprogramar
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showScheduleModal && (
        <SimpleScheduleModal
          onClose={() => setShowScheduleModal(false)}
          onSubmit={handleRequestAppointment}
          isSubmitting={requestAppointment.isPending}
        />
      )}
    </div>
  )
}

interface SimpleScheduleModalProps {
  onClose: () => void
  onSubmit: (data: { preferred_date: string; appointment_type: string; reason: string }) => void
  isSubmitting: boolean
}

function SimpleScheduleModal({ onClose, onSubmit, isSubmitting }: SimpleScheduleModalProps) {
  const [formData, setFormData] = useState({
    preferred_date: '',
    appointment_type: 'consultation',
    reason: '',
  })

  const appointmentTypes = [
    { value: 'consultation', label: 'Consulta' },
    { value: 'follow_up', label: 'Seguimiento' },
    { value: 'checkup', label: 'Chequeo anual' },
    { value: 'telemedicine', label: 'Telemedicina' },
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-background rounded-lg shadow-lg w-full max-w-md">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Solicitar cita</h2>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              ✕
            </button>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Envía una solicitud con tu horario preferido para la cita
          </p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            onSubmit(formData)
          }}
          className="p-6 space-y-4"
        >
          <div className="space-y-2">
            <label className="text-sm font-medium">Tipo de cita</label>
            <select
              value={formData.appointment_type}
              onChange={(e) => setFormData({ ...formData, appointment_type: e.target.value })}
              className="w-full h-10 px-3 rounded-md border bg-background"
            >
              {appointmentTypes.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Fecha y hora preferidas</label>
            <input
              type="datetime-local"
              value={formData.preferred_date}
              onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
              className="w-full h-10 px-3 rounded-md border bg-background"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Motivo de la consulta</label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="Describe brevemente tus síntomas o el motivo de la cita"
              className="w-full px-3 py-2 rounded-md border bg-background min-h-[100px]"
              required
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? 'Enviando...' : 'Solicitar cita'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
